import { Button } from "@loot-os/ui/components/button";
import type { ReactNode } from "react";

import { centsToDollars } from "../lib/format";
import type { PaidAboveChargedRow } from "../lib/paidCosts";

/**
 * What one row compares, in words: the parts of the paid cost and of the limit. A line shipped
 * outside the US counts shipping on both sides; a US line leaves its shipping out.
 */
function aboveChargedText(row: PaidAboveChargedRow): string {
  const item = `item cost ${centsToDollars(row.itemCostCents)}`;
  const charged = `charged ${centsToDollars(row.chargedPriceCents)}`;
  if (!row.countsShipping) {
    return `${item} against ${charged} (shipping ${centsToDollars(row.shippingCents)} not counted)`;
  }
  // A line stored before Loot OS recorded the US shipping has none in its limit, so none is shown.
  const usShipping =
    row.chargedUsShippingCents > 0
      ? ` + ${centsToDollars(row.chargedUsShippingCents)} US shipping`
      : "";
  return (
    `${item} + shipping ${centsToDollars(row.shippingCents)} = ${centsToDollars(row.comparedCostCents)} ` +
    `against ${charged}${usShipping} + ` +
    `${centsToDollars(row.chargedShippingSurchargeCents)} destination shipping = ` +
    centsToDollars(row.limitCents)
  );
}

/**
 * Shown when a StockX paid cost entered for the move to PROCESSING is above the line's limit:
 * on a US line the item cost against the charged price, on a line shipped outside the US the item
 * cost and shipping against the charged price, the US shipping and the surcharge. The size refund
 * floors at 0, so Loot Labs pays the difference. Staff either cancel the order as PRICE_EXCEEDED
 * (`cancelAction`, the order's cancel dialog) or confirm and move on, because they may have
 * bought the pair already.
 */
export function PaidAboveChargedWarning({
  rows,
  labelsByLineId,
  isPending,
  onConfirm,
  cancelAction,
}: {
  rows: readonly PaidAboveChargedRow[];
  labelsByLineId: Readonly<Record<string, string>>;
  isPending: boolean;
  onConfirm: () => void;
  cancelAction: ReactNode;
}) {
  return (
    <div role="alert" className="grid max-w-xl gap-3 rounded-md border border-destructive p-3">
      <p className="text-sm font-semibold text-destructive">
        StockX charged more than the shopper paid.
      </p>
      <ul className="grid gap-1 text-sm">
        {rows.map((row) => (
          <li key={row.lineId}>
            {labelsByLineId[row.lineId] ?? row.lineId}: {aboveChargedText(row)},{" "}
            <span className="font-semibold">{centsToDollars(row.differenceCents)} over</span>
          </li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">
        Cancel the order as PRICE_EXCEEDED, or confirm the cost if the pair is already bought.
      </p>
      <div className="flex flex-wrap gap-2">
        {cancelAction}
        <Button type="button" variant="outline" disabled={isPending} onClick={onConfirm}>
          Confirm and move to PROCESSING
        </Button>
      </div>
    </div>
  );
}
